import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from './common/SafeIcon';
import AddressAutocomplete from './components/AddressAutocomplete';
import OptimizedImage from './components/OptimizedImage';
import LazySection from './components/LazySection';
import { preloadResources, throttle } from './utils/performance';

const { 
  FiPhone, FiMail, FiTruck, FiClock, FiShield, FiCheckCircle, 
  FiArrowRight, FiHome, FiBox, FiPackage, FiZoomIn, FiStar,
  FiMapPin, FiCalendar, FiSmartphone, FiThumbsUp, FiHeart
} = FiIcons;
const { FaWhatsapp } = FaIcons;

function App() {
  const phoneNumber = "+393514527339";
  const whatsappLink = `https://wa.me/393514527339?text=${encodeURIComponent("Ciao, vorrei richiedere un preventivo per un trasloco.")}`;
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    fromAddress: '',
    toAddress: '',
    moveDate: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    preloadResources();
    const handleScroll = throttle(() => {
      setIsScrolled(window.scrollY > 10);
    }, 100);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Tracking Helper per Google Ads
  const trackConversion = (type) => {
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-18163549494', 
        'event_category': 'lead',
        'event_label': type,
      });
      console.log(`Tracked conversion: ${type}`);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    trackConversion('form_submit');
    
    // Simulazione invio e redirect a WhatsApp
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccess(true);
      
      const message = `Ciao, ho appena compilato il modulo sul sito!\n\n` +
        `Nome: ${formData.name}\n` +
        `Telefono: ${formData.phone}\n` +
        `Da: ${formData.fromAddress}\n` +
        `A: ${formData.toAddress}\n` +
        `Periodo: ${formData.moveDate}\n\n` +
        `Vorrei ricevere il preventivo gratuito con lo sconto di 100€!`;
        
      window.open(`https://wa.me/393514527339?text=${encodeURIComponent(message)}`, '_blank');
      
      setFormData({
        name: '', phone: '', fromAddress: '', toAddress: '', moveDate: ''
      });
      
      setTimeout(() => setShowSuccess(false), 5000);
    }, 1500);
  };

  // Scroll fluido che tiene conto dell'header sticky
  const scrollToForm = (e) => {
    if (e) e.preventDefault();
    const el = document.getElementById('preventivo');
    if (el) {
      const headerOffset = 80; 
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const scrollToTop = (e) => {
    if (e) e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getMinDate = () => new Date().toISOString().split('T')[0];

  const services = [
    { icon: FiHome, title: "Traslochi completi", desc: "Gestiamo l'intero trasloco dalla A alla Z, senza pensieri per te." },
    { icon: FiBox, title: "Smontaggio e rimontaggio", desc: "Personale esperto per smontare e rimontare i tuoi mobili con cura." },
    { icon: FiPackage, title: "Imballaggio professionale", desc: "Materiali di alta qualità per proteggere i tuoi oggetti più fragili." },
    { icon: FiTruck, title: "Trasporto mobili", desc: "Furgoni attrezzati e sicuri per ogni tipo di carico." },
    { icon: FiArrowRight, title: "Autoscala", desc: "Servizio autoscala per raggiungere i piani più alti in totale sicurezza." },
    { icon: FiShield, title: "Deposito mobili", desc: "Spazi sicuri e videosorvegliati per custodire i tuoi beni." },
    { icon: FiMapPin, title: "Traslochi nazionali", desc: "Da Bergamo verso tutta Italia con la massima efficienza." }
  ];

  const reviews = [
    {
      highlight: "Massima professionalità e zero stress!",
      text: "Fare un trasloco è sempre stressante, ma con questa ditta è stato tutto semplicissimo. I ragazzi sono stati fantastici: veloci, ordinati e molto educati. Hanno gestito tutto con grande professionalità e il prezzo è stato onesto. Cinque stelle meritatissime!",
      source: "Recensione ricevuta via WhatsApp",
      image: "https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvanBlZw==/a1cc47ab876a067bb77a24772aa06927.jpeg",
      imgPosition: "object-[50%_25%] scale-[1.25] group-hover:scale-[1.3]"
    },
    {
      highlight: "La squadra davvero molto brava.",
      text: "Grazie mille per ieri! La squadra davvero molto brava. Metterò una bella recensione e se serve vi richiamerò.",
      source: "Recensione ricevuta via WhatsApp",
      image: "https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvanBlZw==/362922514d658d02c22104ec71e1bf3b.jpeg",
      imgPosition: "object-[50%_15%] scale-[1.25] group-hover:scale-[1.3]"
    },
    {
      highlight: "Molto attenti ai particolari e nel rispetto.",
      text: "Consiglio vivamente questa società per qualsiasi trasloco molto attenti ai particolari e nel rispetto e attenzione nei loro movimenti non rovinando le mura delle scale. Puntuali negli accordi pattuiti. Ringrazio vivamente la società.",
      source: "Recensione ricevuta via WhatsApp",
      image: "https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvanBlZw==/735d02198c4f1ffb69a4d439fa2c67dc.jpeg",
      imgPosition: "object-[50%_20%] scale-[1.25] group-hover:scale-[1.3]"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0 font-sans">
      
      {/* Promo Banner */}
      <div className="bg-yellow-400 text-yellow-900 text-center py-2 px-4 font-bold text-sm md:text-base shadow-sm relative z-50">
        🎉 100€ DI SCONTO PER I NUOVI CLIENTI - <a href="#preventivo" onClick={scrollToForm} className="underline hover:text-black cursor-pointer">Richiedi ora il tuo preventivo gratuito</a>
      </div>

      {/* Header */}
      <header className={`sticky top-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-white py-4'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <a href="#" onClick={scrollToTop} className="flex items-center space-x-3 cursor-pointer group">
            <OptimizedImage 
              src="https://i.imghippo.com/files/yeSd8206FjQ.png" 
              alt="Traslochi Smart Bergamo" 
              className="h-12 w-12 md:h-14 md:w-14 object-contain group-hover:scale-105 transition-transform" 
              priority={true} 
            />
            <div className="font-bold text-xl md:text-2xl text-blue-900 leading-tight">
              Traslochi<br/><span className="text-blue-600">Smart</span>
            </div>
          </a>
          
          <div className="hidden md:flex items-center space-x-4">
            <a 
              href={`tel:${phoneNumber}`}
              onClick={() => trackConversion('call')}
              className="flex items-center space-x-2 text-blue-900 font-bold hover:text-blue-700 transition-colors"
            >
              <SafeIcon icon={FiPhone} className="text-xl" />
              <span>{phoneNumber}</span>
            </a>
            <a 
              href="#preventivo"
              onClick={scrollToForm}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-full font-bold transition-all shadow-md hover:shadow-lg inline-block"
            >
              Preventivo Gratuito
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-white pt-8 pb-16 md:pt-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-30 transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 transform -translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tight mb-6"
          >
            Traslochi a Bergamo e in tutta Italia, <span className="text-blue-600 relative inline-block">
              senza stress
              <svg className="absolute w-full h-3 -bottom-1 left-0 text-yellow-400" viewBox="0 0 100 10" preserveAspectRatio="none"><path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="3" fill="transparent"/></svg>
            </span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }}
            className="text-lg md:text-2xl text-gray-600 max-w-3xl mx-auto mb-10"
          >
            Ci occupiamo di smontaggio, imballaggio, trasporto e rimontaggio. Richiedi gratuitamente il tuo preventivo.
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.2 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-2xl mx-auto"
          >
            <a 
              href="#preventivo"
              onClick={scrollToForm}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiBox} />
              RICHIEDI PREVENTIVO GRATUITO
            </a>
            <a 
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackConversion('whatsapp')}
              className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FaWhatsapp} className="text-2xl" />
              SCRIVICI SU WHATSAPP
            </a>
            <a 
              href={`tel:${phoneNumber}`}
              onClick={() => trackConversion('call')}
              className="w-full sm:w-auto md:hidden bg-gray-900 hover:bg-black text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiPhone} />
              CHIAMA ORA
            </a>
          </motion.div>
        </div>
      </section>

      {/* Chi siamo */}
      <LazySection className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-sm font-bold text-blue-600 tracking-wider uppercase mb-2">Chi siamo</h2>
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Il tuo trasloco, seguito come se fosse il nostro
              </h3>
              <div className="text-gray-600 space-y-4 text-lg">
                <p>Traslochi Smart Bergamo nasce con un'idea semplice: rendere il trasloco più organizzato, sicuro e soprattutto meno stressante.</p>
                <p>Seguiamo personalmente ogni lavoro, dalla prima richiesta di preventivo fino alla sistemazione finale dei mobili.</p>
                <p>Ci occupiamo di traslochi di appartamenti, case e uffici, offrendo servizi di smontaggio e rimontaggio, imballaggio, trasporto, autoscala e deposito. Operiamo principalmente a Bergamo e provincia, ma organizziamo anche traslochi in tutta Italia.</p>
                <p>La nostra priorità è trattare con cura sia gli oggetti del cliente sia gli ambienti in cui lavoriamo, rispettando gli accordi e mantenendo sempre un contatto diretto durante tutte le fasi del trasloco.</p>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-xl h-full min-h-[300px] lg:min-h-[450px]">
              <OptimizedImage 
                src="https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvanBlZw==/81c2c85518adaffe01f0e670d2b35648.jpeg" 
                alt="Squadra Traslochi Smart Bergamo al lavoro" 
                className="absolute inset-0 w-full h-full object-cover" 
              />
            </div>
          </div>

          {/* 4 Valori */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {[
              { title: "Cura", desc: "Proteggiamo mobili, oggetti e ambienti durante ogni fase del lavoro.", icon: FiShield, color: "text-blue-600", bg: "bg-blue-100" },
              { title: "Organizzazione", desc: "Pianifichiamo il trasloco prima di iniziare, per evitare imprevisti e perdite di tempo.", icon: FiBox, color: "text-yellow-600", bg: "bg-yellow-100" },
              { title: "Disponibilità", desc: "Il cliente può parlare direttamente con noi prima, durante e dopo il trasloco.", icon: FiSmartphone, color: "text-green-600", bg: "bg-green-100" },
              { title: "Affidabilità", desc: "Rispettiamo gli accordi presi e seguiamo il lavoro fino alla conclusione.", icon: FiCheckCircle, color: "text-red-600", bg: "bg-red-100" }
            ].map((feature, i) => (
              <div key={i} className="bg-gray-50 p-6 rounded-2xl border border-gray-100 hover:shadow-md transition-shadow">
                <div className={`w-14 h-14 rounded-xl ${feature.bg} flex items-center justify-center mb-4`}>
                  <SafeIcon icon={feature.icon} className={`text-2xl ${feature.color}`} />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center bg-blue-50 rounded-3xl p-8 md:p-12 border border-blue-100">
            <p className="text-2xl font-bold text-gray-900 mb-6">Raccontaci il tuo trasloco, al resto pensiamo noi.</p>
            <a 
              href="#preventivo"
              onClick={scrollToForm}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2"
            >
              RICHIEDI UN PREVENTIVO GRATUITO
            </a>
          </div>
        </div>
      </LazySection>

      {/* Social Proof Section - Refined */}
      <LazySection className="py-12 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="flex justify-center text-yellow-400 mb-3 text-2xl">
              <SafeIcon icon={FiStar} className="fill-current" />
              <SafeIcon icon={FiStar} className="fill-current" />
              <SafeIcon icon={FiStar} className="fill-current" />
              <SafeIcon icon={FiStar} className="fill-current" />
              <SafeIcon icon={FiStar} className="fill-current" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Chi ha traslocato con noi lo racconta meglio di noi
            </h2>
          </div>

          <div className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-6 -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-3 md:overflow-visible md:snap-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {reviews.map((review, index) => (
              <div key={index} className="flex-none w-[85%] md:w-auto snap-center bg-white p-6 rounded-2xl shadow-lg border border-gray-100 flex flex-col h-full">
                <div className="flex text-yellow-400 mb-4 text-sm">
                  <SafeIcon icon={FiStar} className="fill-current" />
                  <SafeIcon icon={FiStar} className="fill-current" />
                  <SafeIcon icon={FiStar} className="fill-current" />
                  <SafeIcon icon={FiStar} className="fill-current" />
                  <SafeIcon icon={FiStar} className="fill-current" />
                </div>
                
                <h3 className="font-bold text-lg text-gray-900 mb-3 leading-snug min-h-[3.5rem]">
                  "{review.highlight}"
                </h3>
                
                <p className="text-gray-600 italic text-sm mb-6 flex-grow">
                  "{review.text}"
                </p>
                
                <div className="mt-auto">
                  {/* Image container with refined cropping to preserve text readability and hide personal data */}
                  <div 
                    className="group h-32 w-full overflow-hidden rounded-xl mb-4 cursor-pointer relative" 
                    onClick={() => setSelectedImage(review.image)}
                  >
                    <OptimizedImage 
                      src={review.image} 
                      alt="Screenshot recensione" 
                      className={`w-full h-full object-cover transition-transform duration-500 ${review.imgPosition}`} 
                    />
                    <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors"></div>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs text-gray-600 font-medium bg-gray-50 py-2.5 px-3 rounded-lg justify-center border border-gray-100">
                    <SafeIcon icon={FaWhatsapp} className="text-green-500 text-base" />
                    {review.source}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-xl md:text-2xl font-bold text-gray-900 mb-6">
              Hai un trasloco da organizzare? Affidati a chi lo fa ogni giorno.
            </p>
            <a 
              href="#preventivo"
              onClick={scrollToForm}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2"
            >
              RICHIEDI IL PREVENTIVO GRATUITO
            </a>
          </div>
        </div>
      </LazySection>

      {/* Services */}
      <LazySection className="py-20 bg-gray-50 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">I Nostri Servizi</h2>
          <p className="text-center text-gray-600 max-w-2xl mx-auto mb-16">Tutto quello di cui hai bisogno per un trasloco perfetto, su misura per te.</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4 text-blue-600">
                  <SafeIcon icon={service.icon} className="text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600 mb-6 flex-grow">{service.desc}</p>
                <a 
                  href="#preventivo"
                  onClick={scrollToForm}
                  className="text-blue-600 font-semibold flex items-center gap-2 hover:text-blue-800 transition-colors mt-auto inline-flex"
                >
                  Preventivo <SafeIcon icon={FiArrowRight} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </LazySection>

      {/* Real Photos / Gallery */}
      <LazySection className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">Alcuni dei nostri traslochi</h2>
          <p className="text-center text-gray-600 max-w-2xl mx-auto mb-12 text-lg">Lavori reali effettuati dalla nostra squadra</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Immagine Principale Prima/Dopo con Etichette */}
            <div 
              className="group relative col-span-2 md:col-span-4 aspect-video md:aspect-[21/9] rounded-xl overflow-hidden shadow-sm cursor-pointer hover:shadow-lg transition-all" 
              onClick={() => setSelectedImage("https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvanBlZw==/f796abf138343d7cb636e57d9ec9d40a.jpeg")}
            >
              <OptimizedImage 
                src="https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvanBlZw==/f796abf138343d7cb636e57d9ec9d40a.jpeg" 
                alt="Prima e Dopo Trasloco" 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
              />
              {/* Etichette PRIMA e DOPO */}
              <div className="absolute top-3 left-3 md:top-6 md:left-6 bg-black/75 text-white px-3 py-1 md:px-4 md:py-1.5 rounded-md font-bold text-xs md:text-sm tracking-widest backdrop-blur-sm shadow-lg pointer-events-none">
                PRIMA
              </div>
              <div className="absolute top-3 right-3 md:top-6 md:right-6 bg-black/75 text-white px-3 py-1 md:px-4 md:py-1.5 rounded-md font-bold text-xs md:text-sm tracking-widest backdrop-blur-sm shadow-lg pointer-events-none">
                DOPO
              </div>
            </div>
            
            {/* Altre Foto Reali */}
            {[
              "https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvanBlZw==/81c2c85518adaffe01f0e670d2b35648.jpeg",
              "https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvanBlZw==/522e27697244cdd0ab4b1d96121786aa.jpeg",
              "https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvanBlZw==/27fcb17661e4721a24555225228a7493.jpeg",
              "https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvanBlZw==/a600c795c30301a2b001e22bd58c0a23.jpeg"
            ].map((url, i) => (
              <div key={i} className="group aspect-square rounded-xl overflow-hidden shadow-sm cursor-pointer hover:shadow-lg transition-all" onClick={() => setSelectedImage(url)}>
                <OptimizedImage src={url} alt={`Fase trasloco ${i+1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Vuoi un trasloco organizzato dall'inizio alla fine?</p>
            <a 
              href="#preventivo"
              onClick={scrollToForm}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiBox} />
              RICHIEDI IL TUO PREVENTIVO GRATUITO
            </a>
          </div>
        </div>
      </LazySection>

      {/* How it works */}
      <LazySection className="py-20 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">Come funziona</h2>
          
          <div className="grid md:grid-cols-3 gap-12 text-center relative">
            <div className="hidden md:block absolute top-1/2 left-[16%] right-[16%] h-0.5 bg-blue-700 -translate-y-1/2 z-0"></div>
            
            {[
              { num: "1", title: "Raccontaci il tuo trasloco", desc: "Mandaci le informazioni o alcune fotografie tramite il modulo o WhatsApp." },
              { num: "2", title: "Ricevi il preventivo", desc: "Valutiamo il lavoro e prepariamo gratuitamente il preventivo su misura." },
              { num: "3", title: "Pensiamo noi al resto", desc: "La nostra squadra gestisce il trasloco dal ritiro alla sistemazione finale." }
            ].map((step, i) => (
              <div key={i} className="relative z-10 flex flex-col items-center">
                <div className="w-16 h-16 bg-yellow-400 text-blue-900 rounded-full flex items-center justify-center text-2xl font-bold mb-6 border-4 border-blue-900 shadow-xl">
                  {step.num}
                </div>
                <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                <p className="text-blue-200">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </LazySection>

      {/* Contact Form Section - Ora racchiusa in una vera section con ID per garantire che funzioni sempre lo scroll */}
      <section id="preventivo">
        <LazySection className="py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
              <div className="bg-blue-600 p-8 text-center text-white">
                <h2 className="text-3xl font-bold mb-2">Richiedi Preventivo Gratuito</h2>
                <p className="text-blue-100">Compila il modulo veloce, ti risponderemo in pochissimo tempo.</p>
                <div className="inline-block mt-4 bg-yellow-400 text-yellow-900 px-4 py-1 rounded-full text-sm font-bold">
                  🎁 100€ di Sconto per nuovi clienti
                </div>
              </div>
              
              <div className="p-8 md:p-12">
                {showSuccess ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl">
                      <SafeIcon icon={FiCheckCircle} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Richiesta Inviata!</h3>
                    <p className="text-gray-600 mb-6">Stiamo aprendo WhatsApp per completare la richiesta.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Nome e Cognome *</label>
                        <input 
                          type="text" name="name" value={formData.name} onChange={handleInputChange} required 
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none" 
                          placeholder="Es. Mario Rossi" 
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Telefono *</label>
                        <input 
                          type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required 
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none" 
                          placeholder="Es. 333 1234567" 
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Da dove parti? *</label>
                        <AddressAutocomplete 
                          value={formData.fromAddress} 
                          onChange={(value) => handleAddressChange('fromAddress', value)} 
                          placeholder="Città o indirizzo..." 
                          required={true} 
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Dove vai? *</label>
                        <AddressAutocomplete 
                          value={formData.toAddress} 
                          onChange={(value) => handleAddressChange('toAddress', value)} 
                          placeholder="Città o indirizzo..." 
                          required={true} 
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Periodo indicativo *</label>
                      <input 
                        type="date" name="moveDate" value={formData.moveDate} onChange={handleInputChange} required min={getMinDate()} 
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none" 
                      />
                    </div>

                    <button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] shadow-lg flex items-center justify-center gap-2 mt-8 disabled:opacity-70 cursor-pointer"
                    >
                      {isSubmitting ? (
                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                      ) : (
                        <>RICHIEDI PREVENTIVO GRATUITO <SafeIcon icon={FiArrowRight} /></>
                      )}
                    </button>
                    <p className="text-center text-sm text-gray-500 mt-4">Nessun impegno. Preventivo 100% gratuito.</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </LazySection>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 pb-24 md:pb-12 border-t-4 border-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8 text-center md:text-left">
            <div className="flex flex-col items-center md:items-start">
              <a href="#" onClick={scrollToTop} className="flex items-center space-x-3 mb-4 cursor-pointer group">
                <div className="bg-white p-1 rounded-lg group-hover:scale-105 transition-transform">
                  <OptimizedImage src="https://i.imghippo.com/files/yeSd8206FjQ.png" alt="Logo" className="h-10 w-10 object-contain" />
                </div>
                <h3 className="text-xl font-bold">TraslochiSmartBergamo</h3>
              </a>
              <p className="text-gray-400 max-w-xs">Il tuo partner di fiducia per traslochi professionali a Bergamo e in tutta Italia, senza stress.</p>
            </div>
            
            <div className="flex flex-col items-center md:items-start">
              <h4 className="text-lg font-bold mb-4 text-white">Contatti Rapidi</h4>
              <div className="space-y-4">
                <a href={`tel:${phoneNumber}`} onClick={() => trackConversion('call')} className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors cursor-pointer">
                  <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center"><SafeIcon icon={FiPhone} /></div>
                  <span className="font-semibold">{phoneNumber}</span>
                </a>
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" onClick={() => trackConversion('whatsapp')} className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors cursor-pointer">
                  <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center text-green-400"><SafeIcon icon={FaWhatsapp} className="text-xl" /></div>
                  <span className="font-semibold">Chatta su WhatsApp</span>
                </a>
              </div>
            </div>
            
            <div className="flex flex-col items-center md:items-start">
              <h4 className="text-lg font-bold mb-4 text-white">Servizi Principali</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Traslochi Bergamo</li>
                <li>Smontaggio e Rimontaggio</li>
                <li>Imballaggio Professionale</li>
                <li>Trasporto Mobili</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Traslochi Smart Bergamo. Tutti i diritti riservati.</p>
          </div>
        </div>
      </footer>

      {/* Mobile Sticky Bottom Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-5px_15px_rgba(0,0,0,0.05)] z-50 flex">
        <a 
          href={`tel:${phoneNumber}`}
          onClick={() => trackConversion('call')}
          className="flex-1 py-3 flex flex-col items-center justify-center text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
        >
          <SafeIcon icon={FiPhone} className="text-xl mb-1" />
          <span className="text-[10px] font-bold uppercase tracking-wider">Chiama</span>
        </a>
        <div className="w-[1px] bg-gray-200"></div>
        <a 
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackConversion('whatsapp')}
          className="flex-1 py-3 flex flex-col items-center justify-center text-green-500 hover:bg-green-50 transition-colors cursor-pointer"
        >
          <SafeIcon icon={FaWhatsapp} className="text-xl mb-1" />
          <span className="text-[10px] font-bold uppercase tracking-wider">WhatsApp</span>
        </a>
        <div className="w-[1px] bg-gray-200"></div>
        <a 
          href="#preventivo"
          onClick={scrollToForm}
          className="flex-1 py-3 flex flex-col items-center justify-center bg-blue-600 text-white hover:bg-blue-700 transition-colors cursor-pointer"
        >
          <SafeIcon icon={FiBox} className="text-xl mb-1" />
          <span className="text-[10px] font-bold uppercase tracking-wider">Preventivo</span>
        </a>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <motion.div 
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} 
          className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center p-4" 
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl w-full flex justify-center">
            <button onClick={() => setSelectedImage(null)} className="absolute -top-12 right-0 md:top-4 md:-right-12 text-white hover:text-gray-300 p-2">
              <SafeIcon icon={FiIcons.FiX} className="text-3xl" />
            </button>
            <img src={selectedImage} alt="Ingrandimento" className="max-h-[85vh] max-w-full object-contain rounded-lg shadow-2xl" />
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default App;